/**
 * Filter-persistence helper shared by the report resolvers.
 *
 * The persistence wiring caches the last-used filter form values under a
 * component's `<storageId>_filterValues` key (see {@link ComponentStorageKeyProvider}).
 * On re-entering a page its resolver rebuilds the filter controls from scratch;
 * this helper re-applies the cached values onto those freshly built controls so
 * the user sees the same selection they left with.
 *
 * Kept structurally typed (no `FilterControl`/`BaseControl` import) so it stays a
 * dependency-free, alias-free pure function that is trivial to unit test.
 */

export interface CachedFilterControlValues {
    [controlName: string]: string | number | boolean | string[] | number[] | boolean[];
}

interface CacheableControl {
    name: string;
    value: string | number | boolean | string[] | number[] | boolean[];
}

interface CacheableFilterControl {
    controls: CacheableControl[];
}

export function applyCachedFilterControlValues(
    filterControls: CacheableFilterControl[],
    cachedValues: CachedFilterControlValues
): void {
    if (!filterControls || !cachedValues) {
        return;
    }

    filterControls.forEach((filterControl: CacheableFilterControl) => {
        filterControl.controls.forEach((control: CacheableControl) => {
            control.value = cachedValues[control.name] ?? control.value;
        });
    });
}
